import { PRODUCT } from '@oskecare/fe-utilities/lib/constants/dataSources';
import { cacheWithErrorCheck } from '../../helpers.js';
import ProductList from './models/ProductList.js';
import { getIdentityQueryAndCacheKey } from './helpers.js';
import {
  PRODUCT_ID_E_SIM_ACCESS_SERVICE,
  PRODUCT_ID_DATA_BUTTON,
  PRODUCT_ID_MASTER_SN,
  PRODUCT_ID_SHS,
  INVENTORY_FILTER_INCLUDE_DETAILS,
  PRODUCT_ID_TOGETHER_FREE_OWNER,
  PRODUCT_ID_TOGETHER_FREE_MEMBER,
  PRODUCT_ID_LOVE,
  PRODUCT_ID_CHAINED_PREPAID_BENEFIT_OFFER_DETAILS,
  PRODUCT_ID_CHAINED_PREPAID_BENEFIT_INITIAL_OFFER,
  FILTER_ONLY_BASIC_PRODUCT,
  PRODUCT_ID_AUTOMATIC_REFILL,
  PRODUCT_ID_PREPAID_STARTER_PACK_VOICE_OFFER,
  PRODUCT_ID_PREPAID_STARTER_PACK_DATA_OFFER,
} from './constants.js';

export default class Inventory {
  /**
   * @param {string} apiUrl
   * @param {Map<string, Promise<*>>} cache
   * @param {function} request
   * @param {CustomerDataManager} customerDataManager
   */
  constructor({ apiUrl, cache, request, customerDataManager }) {
    this._apiUrl = apiUrl;
    this._cache = cache;
    this._request = request;
    this._customerDataManager = customerDataManager;
  }

  /**
   * @param {string} sn
   * @param {string} billingAccountId
   * @param {?string} productId
   * @param {?string} filter
   * @param {boolean} isNonessential
   * @return {Promise<ProductList>}
   * @private
   */
  _productRequest({ sn, billingAccountId, productId = null, filter = null, isNonessential }) {
    const [identityQuery, cacheKey] = getIdentityQueryAndCacheKey({
      sn,
      billingAccountId,
    });

    return cacheWithErrorCheck(
      this._cache,
      `${cacheKey}${productId ? `.productId-${productId}` : ''}${filter ? `.filter-${filter}` : ''}`,
      () =>
        this._request({
          method: 'GET',
          route: this._apiUrl,
          query: {
            ...identityQuery,
            'productSpecification.id': productId,
            filter,
          },
          headers: {
            'X-Varnish-Cache': 'forceUpdateCache',
          },
          dataSource: PRODUCT,
          isNonessential,
        }).then(data => new ProductList(data)),
    );
  }

  /**
   * @param {string} sn
   * @param {boolean} isNonessential
   * @return {Promise<ProductList>}
   */
  getSharingServices({ sn, isNonessential }) {
    return this._productRequest({
      sn,
      productId: PRODUCT_ID_SHS,
      isNonessential,
    });
  }

  /**
   * @param {string} sn
   * @param {boolean} isNonessential
   * @return {Promise<ProductList>}
   */
  getDataButton({ sn, isNonessential }) {
    return this._productRequest({
      sn,
      productId: PRODUCT_ID_DATA_BUTTON,
      isNonessential,
    });
  }

  /**
   * Information about customers active SIM (type, number, status, PUK, etc.)
   *
   * @param {string} sn
   * @param {boolean} isNonessential
   * @return {Promise<ProductList>}
   */
  getAccessService({ sn, isNonessential }) {
    return this._productRequest({
      sn,
      productId: PRODUCT_ID_E_SIM_ACCESS_SERVICE,
      isNonessential,
    });
  }

  /**
   * @return {Promise<ProductList>}
   */
  async getMasterSn() {
    const { billingAccountId } = (await this._customerDataManager.getCustomerData()).cnData;
    return this._productRequest({
      billingAccountId,
      filter: INVENTORY_FILTER_INCLUDE_DETAILS,
      productId: PRODUCT_ID_MASTER_SN,
    });
  }

  /**
   * @param {string} sn
   * @param {boolean} isNonessential
   * @return {Promise<ProductList>}
   */
  getTogetherFree({ sn, isNonessential }) {
    return this._productRequest({
      sn,
      filter: INVENTORY_FILTER_INCLUDE_DETAILS,
      productId: `${PRODUCT_ID_TOGETHER_FREE_OWNER},${PRODUCT_ID_TOGETHER_FREE_MEMBER}`,
      isNonessential,
    });
  }

  /**
   * @param {boolean} isNonessential
   * @return {Promise<ProductList>}
   */
  async getLove(isNonessential) {
    const { billingAccountId } = (await this._customerDataManager.getCustomerData()).cnData;
    return this._productRequest({
      billingAccountId,
      productId: PRODUCT_ID_LOVE,
      isNonessential,
    });
  }

  /**
   * Returns pseudo-products derived from non-product Tariff Options which carry characteristics
   * required to determine correct state of "chained prepaid benefit" offer, for two offer variants
   * (primary and secondary).
   *
   * Also return initial offer product if it's active, this is so that we can recognize it as active,
   * before the detail pseudo-products are updated with current status of this service shortly after
   * the customer orders it.
   *
   * @param {string} sn
   * @return {Promise<ProductList>}
   */
  getChainedPrepaidBenefits(sn) {
    return this._productRequest({
      sn,
      filter: INVENTORY_FILTER_INCLUDE_DETAILS,
      productId: `${PRODUCT_ID_CHAINED_PREPAID_BENEFIT_OFFER_DETAILS},${PRODUCT_ID_CHAINED_PREPAID_BENEFIT_INITIAL_OFFER}`,
    });
  }

  /**
   * @param {string} sn
   * @param {boolean} isNonessential
   * @return {Promise<ProductList>}
   */
  getBasicProduct(sn, isNonessential = false) {
    return this._productRequest({
      sn,
      filter: FILTER_ONLY_BASIC_PRODUCT,
      isNonessential,
    });
  }

  /**
   * @param {string} sn
   * @return {Promise<ProductList>}
   */
  getAutomaticRefill(sn) {
    return this._productRequest({
      sn,
      productId: PRODUCT_ID_AUTOMATIC_REFILL,
    });
  }

  /**
   * @param {string} sn
   * @return {Promise<ProductList>}
   */
  getPrepaidStarterPackOffer(sn) {
    return this._productRequest({
      sn,
      filter: INVENTORY_FILTER_INCLUDE_DETAILS,
      productId: `${PRODUCT_ID_PREPAID_STARTER_PACK_VOICE_OFFER},${PRODUCT_ID_PREPAID_STARTER_PACK_DATA_OFFER}`,
    });
  }
}
